import { supabase } from '../lib/supabase';
import { sendInvitationEmail, sendProfileUpdateEmail } from './emailService';

const normalizeProfile = (profile) => ({
  id: profile?.id,
  email: profile?.email || '',
  fullName: profile?.full_name || '',
  phone: profile?.phone || '',
  role: profile?.role || '',
  region: profile?.region || '',
  permissions: Array.isArray(profile?.permissions) ? profile.permissions : [],
  avatarUrl: profile?.avatar_url || '',
  isActive: profile?.is_active ?? true,
  regionalPlayerRegistrationOverrideUntil:
    profile?.regional_player_registration_override_until || null,
  createdAt: profile?.created_at,
  updatedAt: profile?.updated_at,
});

const buildProfileChanges = (before, after) => {
  const changes = {};
  const pairs = [
    ['full_name', before?.fullName, after?.fullName],
    ['phone', before?.phone || 'Not set', after?.phone || 'Not set'],
    ['role', before?.role, after?.role],
    ['region', before?.region || 'Not set', after?.region || 'Not set'],
    [
      'status',
      before?.isActive ? 'Active' : 'Inactive',
      after?.isActive ? 'Active' : 'Inactive',
    ],
  ];

  for (const [key, oldVal, newVal] of pairs) {
    if (String(oldVal ?? '') !== String(newVal ?? '')) {
      changes[key] = { old: oldVal ?? 'Not set', new: newVal ?? 'Not set' };
    }
  }
  return changes;
};

export const userService = {
  async listUsers({ searchTerm, role } = {}) {
    let query = supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (searchTerm) {
      const sanitized = searchTerm?.replace(/[%]/g, '');
      query = query?.or(
        `full_name.ilike.%${sanitized}%,email.ilike.%${sanitized}%,phone.ilike.%${sanitized}%`
      );
    }

    if (role && role !== 'all') {
      query = query?.eq('role', role);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error?.message || 'Failed to load users');
    }

    return data?.map(normalizeProfile) ?? [];
  },

  async createUser({ email, password, fullName, role, phone, region, permissions = [] }) {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';

      const response = await fetch(`${apiUrl}/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          fullName,
          role,
          phone: phone || null,
          region: role === 'region_admin' ? region || null : null,
          permissions: Array.isArray(permissions) ? permissions : [],
        }),
      });

      if (!response.ok) {
        let errorData;
        let errorText;
        try {
          errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('User creation API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });
        throw new Error(
          errorData.message || errorData.error || `Failed to create user (${response.status})`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create user');
      }

      const profile = {
        id: result.user.id,
        email: result.user.email,
        full_name: result.user.fullName,
        role: result.user.role,
        phone: result.user.phone,
        region: result.user.region || null,
        permissions: result.user.permissions || [],
        avatar_url: null,
        is_active: true,
        created_at: result.user.createdAt,
        updated_at: result.user.createdAt,
      };

      try {
        const loginUrl = `${window.location.origin}/login`;
        await sendInvitationEmail({
          to: email,
          fullName,
          email,
          password,
          role,
          loginUrl,
        });
      } catch (emailError) {
        console.error('Failed to send invitation email:', emailError);
      }

      return normalizeProfile(profile);
    } catch (error) {
      throw new Error(error?.message || 'Failed to create user');
    }
  },

  async getUserById(userId) {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', userId)
      ?.single();

    if (error) {
      throw new Error(error?.message || 'Failed to load user');
    }

    return normalizeProfile(data);
  },

  async updateUserProfile(userId, { fullName, phone, role, region, isActive, permissions }) {
    const before = await this.getUserById(userId);

    const payload = {};

    if (fullName !== undefined) payload.full_name = fullName;
    if (phone !== undefined) payload.phone = phone;
    if (role !== undefined) payload.role = role;
    if (role === 'region_admin') {
      payload.region = region || null;
    } else if (role !== undefined && role !== 'region_admin') {
      payload.region = null;
    } else if (region !== undefined) {
      payload.region = region || null;
    }
    if (permissions !== undefined) {
      payload.permissions = Array.isArray(permissions) ? permissions : [];
    }
    if (isActive !== undefined) payload.is_active = isActive;

    payload.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(payload)
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (error) {
      throw new Error(error?.message || 'Failed to update user profile');
    }

    const updated = normalizeProfile(data);
    const changes = buildProfileChanges(before, updated);

    if (Object.keys(changes).length > 0 && updated.email) {
      try {
        await sendProfileUpdateEmail({
          to: updated.email,
          fullName: updated.fullName || before.fullName || 'User',
          changes,
        });
      } catch (emailError) {
        console.error('Failed to send account update email:', emailError);
      }
    }

    return updated;
  },

  async getRegionalPlayerRegistrationWindow() {
    const { data, error } = await supabase
      ?.from('registration_windows')
      ?.select('*')
      ?.eq('window_key', 'regional_team_players')
      ?.maybeSingle();

    if (error) {
      throw new Error(error?.message || 'Failed to load registration window');
    }

    return {
      windowKey: data?.window_key || 'regional_team_players',
      enabled: data?.enabled ?? false,
      opensAt: data?.opens_at || null,
      closesAt: data?.closes_at || null,
      updatedAt: data?.updated_at || null,
      updatedBy: data?.updated_by || null,
    };
  },

  async updateRegionalPlayerRegistrationWindow({ enabled, opensAt, closesAt }) {
    const payload = {
      window_key: 'regional_team_players',
      enabled: !!enabled,
      opens_at: opensAt || null,
      closes_at: closesAt || null,
      updated_at: new Date()?.toISOString(),
    };

    const {
      data: { user },
    } = await supabase?.auth?.getUser();
    if (user?.id) payload.updated_by = user.id;

    const { data, error } = await supabase
      ?.from('registration_windows')
      ?.upsert(payload, { onConflict: 'window_key' })
      ?.select()
      ?.single();

    if (error) {
      throw new Error(error?.message || 'Failed to update registration window');
    }

    return data;
  },

  async checkRegionalPlayerRegistrationAccess(userId = null) {
    const args = userId ? { target_user_id: userId } : {};
    const { data, error } = await supabase?.rpc(
      'can_manage_regional_player_registration',
      args
    );
    if (error) {
      throw new Error(error?.message || 'Failed to check registration access');
    }
    return !!data;
  },

  async setRegionalPlayerRegistrationOverride(userId, overrideUntilIso) {
    if (!userId) throw new Error('User is required');
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({
        regional_player_registration_override_until: overrideUntilIso || null,
        updated_at: new Date()?.toISOString(),
      })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (error) {
      throw new Error(error?.message || 'Failed to update user override');
    }
    return normalizeProfile(data);
  },

  async toggleUserActive(userId, isActive) {
    return this.updateUserProfile(userId, { isActive });
  },

  async deleteUser(userId) {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
      const response = await fetch(`${apiUrl}/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to delete user');
      }

      return true;
    } catch (error) {
      throw new Error(error?.message || 'Failed to delete user');
    }
  },

  async sendPasswordResetEmail({ email, fullName }) {
    if (!email?.trim()) {
      throw new Error('User email is required to send a password reset link');
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
      const response = await fetch(`${apiUrl}/admin-send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          fullName: fullName || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || 'Failed to send password reset email'
        );
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to send password reset email');
      }

      return result;
    } catch (error) {
      throw new Error(error?.message || 'Failed to send password reset email');
    }
  },

  async uploadAvatar(userId, file) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user || user.id !== userId) {
        throw new Error('Not authorized to upload avatar');
      }

      const fileExt = file?.name?.split('.').pop();
      const fileName = `avatars/${userId}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;

      const { data, error } = await supabase?.storage
        ?.from('user-avatars')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        if (error?.message?.includes('Bucket not found') || error?.message?.includes('bucket')) {
          const { data: fallbackData, error: fallbackError } = await supabase?.storage
            ?.from('avatars')
            ?.upload(fileName, file, {
              cacheControl: '3600',
              upsert: false,
            });

          if (fallbackError) {
            throw new Error(
              'Storage bucket not configured. Please create a bucket named "user-avatars" or "avatars" in Supabase Storage.'
            );
          }

          const {
            data: { publicUrl },
          } = supabase?.storage?.from('avatars')?.getPublicUrl(fallbackData?.path);
          return publicUrl;
        }
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase?.storage?.from('user-avatars')?.getPublicUrl(data?.path);

      return publicUrl;
    } catch (error) {
      throw new Error(error?.message || 'Failed to upload avatar');
    }
  },

  async updatePassword(currentPassword, newPassword) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      const { error: updateError } = await supabase?.auth?.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update password');
      }

      return { success: true };
    } catch (error) {
      throw new Error(error?.message || 'Failed to update password');
    }
  },
};
